import React, { useState, useEffect } from 'react';
import { X, Heart, Info, Shield, Phone } from 'lucide-react';

/**
 * Relationship Type Form Component
 * 
 * Used for creating and editing relationship types for parents/guardians.
 * 
 * @param {Object} props
 * @param {Object} props.initialData - Relationship type data for editing (null for new)
 * @param {Function} props.onSubmit - Called with form data on submit
 * @param {Function} props.onCancel - Called when cancel is clicked
 */
const RelationshipTypeForm = ({ initialData, onSubmit, onCancel }) => {
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPrimary: false,
    isEmergencyContact: true,
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with data when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        isPrimary: initialData.isPrimary || false,
        isEmergencyContact: initialData.isEmergencyContact !== false,
        isActive: initialData.isActive !== false,
      });
    }
  }, [initialData]);

  // Validate form
  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Relationship type name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        isPrimary: formData.isPrimary,
        isEmergencyContact: formData.isEmergencyContact,
        isActive: formData.isActive,
      });
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
            <Heart className="text-pink-600" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {isEditing ? 'Edit Relationship Type' : 'Add Relationship Type'}
            </h3>
            <p className="text-sm text-gray-500">
              {isEditing ? 'Update relationship type details' : 'Create a new relationship type for parents/guardians'}
            </p>
          </div>
        </div>
        <button 
          onClick={onCancel} 
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={isSubmitting}
        >
          <X size={20} />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Name Field */}
        <div>
          <label className="form-label">
            Relationship Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Grandmother"
            disabled={isSubmitting}
            autoFocus
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <Info size={14} className="mr-1" />
              {errors.name}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            This is how the relationship will appear when assigning to parents/guardians
          </p>
        </div>

        {/* Description Field */}
        <div>
          <label className="form-label">Description</label>
          <textarea
            className={`form-input ${errors.description ? 'border-red-300 focus:ring-red-500' : ''}`}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Optional description of this relationship"
            rows={3}
            disabled={isSubmitting}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <Info size={14} className="mr-1" />
              {errors.description}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {formData.description.length}/500 characters
          </p>
        </div>

        {/* Permissions Section */}
        <div className="space-y-4">
          <label className="form-label flex items-center space-x-2">
            <Shield size={16} />
            <span>Permissions</span>
          </label>

          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            {/* Primary Contact */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPrimary}
                onChange={(e) => handleChange('isPrimary', e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 mt-0.5"
                disabled={isSubmitting}
              />
              <div>
                <span className="font-medium text-gray-700">Can be primary contact</span>
                <p className="text-sm text-gray-500">
                  This relationship type can be designated as the primary contact for a student
                </p>
              </div>
            </label>

            {/* Emergency Contact */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isEmergencyContact}
                onChange={(e) => handleChange('isEmergencyContact', e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 mt-0.5"
                disabled={isSubmitting}
              />
              <div>
                <span className="font-medium text-gray-700">Can be emergency contact</span>
                <p className="text-sm text-gray-500">
                  This relationship type can be listed as an emergency contact
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Permission Preview */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <p className="text-sm font-medium text-blue-900 mb-2">Permission Preview</p>
          <div className="flex flex-wrap gap-2">
            {formData.isPrimary && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                <Shield size={12} className="mr-1" />
                Primary Contact
              </span>
            )}
            {formData.isEmergencyContact && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
                <Phone size={12} className="mr-1" />
                Emergency Contact
              </span>
            )}
            {!formData.isPrimary && !formData.isEmergencyContact && (
              <span className="text-sm text-blue-600">No special permissions</span>
            )}
          </div>
        </div>

        {/* Active Status */}
        {isEditing && (
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 mt-0.5"
              disabled={isSubmitting}
            />
            <div>
              <label htmlFor="isActive" className="font-medium text-gray-700 cursor-pointer">
                Active
              </label>
              <p className="text-sm text-gray-500">
                Inactive relationship types won't appear in dropdowns when creating new parents
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{isEditing ? 'Save Changes' : 'Add Relationship Type'}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RelationshipTypeForm;
